import { Form, Select } from "antd";
import _ from "lodash";
import PropTypes from "prop-types";
import { useEffect } from "react";

import { useGetAllBusinessQuery } from "@/redux/api/businessApi";
import { useGetAllDeviceQuery } from "@/redux/api/deviceApi";
import { useGetAllDeviceParameterQuery } from "@/redux/api/deviceParameterApi";
import { useGetAllSiteLocationQuery } from "@/redux/api/siteLocationApi";

const defaultProps = {
  show: true,
  isMulti: true,
  params: {},
  extra: undefined,
  formItemsProps: {},
  selectProps: {},
};

const filterOption = (input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

const isEmpty = (val) => {
  if (Array.isArray(val)) return val.length === 0;
  return !val;
};

// normalize single/multi value
const normalizeValue = (val, isMulti) => {
  if (isMulti) return Array.isArray(val) ? val : val ? [val] : [];
  return Array.isArray(val) ? val[0] : val;
};

const getEntityIds = (val) => {
  if (!val) return undefined;
  if (typeof val === "string") return val;
  if (Array.isArray(val)) {
    const ids = val.map((item) => (typeof item === "object" ? item?._id || item?.id : item)).filter(Boolean);
    if (ids.length === 0) return undefined;
    return ids.length === 1 ? ids[0] : ids;
  }
  if (typeof val === "object") return val._id || val.id;
  return undefined;
};

const BusinessSerializeSelector = ({
  business: businessProp = {},
  site: siteProp = {},
  device: deviceProp = {},
  parameter: parameterProp = {},
  form,
  names = [],
  listName = [],
}) => {
  const business = { ...defaultProps, ...businessProp };
  const site = { ...defaultProps, ...siteProp };
  const device = { ...defaultProps, ...deviceProp };
  const parameter = { ...defaultProps, ...parameterProp };

  const businessName = business.altName || "business";
  const siteName = site.altName || "site";
  const deviceName = device.altName || "device";
  const parameterName = parameter.altName || "yAxis";

  // safe path builder
  const getName = (field) => (listName?.length ? [...listName, ...names, field] : [...names, field]);

  // Watch values & normalize IDs
  const rawBusiness = Form.useWatch(getName(businessName), form);
  const rawSite = Form.useWatch(getName(siteName), form);
  const rawDevice = Form.useWatch(getName(deviceName), form);
  const selectedYAxis = Form.useWatch(getName(parameterName), form);

  const selectedBusiness = getEntityIds(rawBusiness);
  const selectedSite = getEntityIds(rawSite);
  const selectedDevice = getEntityIds(rawDevice);

  // Normalized params
  const busParamId = getEntityIds(business.params?._id);
  const siteParamId = getEntityIds(site.params?._id);

  // Queries
  // currentData (not data) so a previous dashboard's business list is never used while the new one loads
  const { currentData: currentBusinessList, isFetching: businessLoading } = useGetAllBusinessQuery({
    limit: 500,
    page: 1,
    ...business.params,
    ...(busParamId ? { _id: busParamId } : {}),
  });
  const businessList = currentBusinessList || {};
  const { data: siteLocationList = {}, isFetching: siteLoading } = useGetAllSiteLocationQuery(
    {
      [businessName]: selectedBusiness,
      businesses: selectedBusiness,
      limit: 500,
      ...site.params,
      ...(siteParamId ? { _id: siteParamId } : {}),
    },
    { skip: isEmpty(selectedBusiness) },
  );

  const { data: deviceList = {}, isFetching: deviceLoading } = useGetAllDeviceQuery(
    { [siteName]: selectedSite, limit: 500, ...device.params },
    { skip: isEmpty(selectedSite) },
  );

  const { data: parameterList = {}, isFetching: parameterLoading } = useGetAllDeviceParameterQuery(
    {
      [deviceName]: selectedDevice,
      limit: 500,
      populate: "device device.site",
      ...parameter.params,
    },
    { skip: isEmpty(selectedDevice) },
  );

  // auto select if business is empty
  useEffect(() => {
    const currentVal = form.getFieldValue(getName(businessName));
    if (isEmpty(currentVal)) {
      if (busParamId && businessList?.data?.some((b) => b._id === busParamId || (Array.isArray(busParamId) && busParamId.includes(b._id)))) {
        form.setFieldValue(getName(businessName), normalizeValue(busParamId, business.isMulti));
      } else if (businessList?.data?.length === 1) {
        form.setFieldValue(getName(businessName), normalizeValue(businessList.data[0]._id, business.isMulti));
      }
    }
  }, [businessList, busParamId]);

  // Auto-select & prune site selection based on available siteLocationList
  useEffect(() => {
    if (isEmpty(selectedBusiness)) return;
    if (siteLoading || !siteLocationList?.data) return;

    const rawVal = form.getFieldValue(getName(siteName));
    const currentVal = getEntityIds(rawVal);
    const availableSiteIds = siteLocationList.data.map((s) => s._id);

    if (!isEmpty(currentVal)) {
      const currentArray = Array.isArray(currentVal) ? currentVal : [currentVal];
      const validArray = currentArray.filter((id) => availableSiteIds.includes(id));

      if (validArray.length !== currentArray.length) {
        form.setFieldValue(
          getName(siteName),
          validArray.length ? normalizeValue(validArray, site.isMulti) : undefined
        );
      }
    } else {
      if (siteParamId) {
        const siteParamIds = Array.isArray(siteParamId) ? siteParamId : [siteParamId];
        const validSiteIds = availableSiteIds.filter((id) => siteParamIds.includes(id));
        if (validSiteIds.length) {
          form.setFieldValue(getName(siteName), normalizeValue(validSiteIds, site.isMulti));
        }
      } else if (availableSiteIds.length === 1) {
        form.setFieldValue(getName(siteName), normalizeValue(availableSiteIds[0], site.isMulti));
      }
    }
  }, [siteLocationList, siteLoading, selectedBusiness, siteParamId]);

  // Prune device selection based on available deviceList
  useEffect(() => {
    if (isEmpty(selectedSite)) return;
    if (deviceLoading || !deviceList?.data) return;

    const rawVal = form.getFieldValue(getName(deviceName));
    const currentVal = getEntityIds(rawVal);
    const availableDeviceIds = deviceList.data.map((d) => d._id);

    if (!isEmpty(currentVal)) {
      const currentArray = Array.isArray(currentVal) ? currentVal : [currentVal];
      const validArray = currentArray.filter((id) => availableDeviceIds.includes(id));

      if (validArray.length !== currentArray.length) {
        form.setFieldValue(
          getName(deviceName),
          validArray.length ? normalizeValue(validArray, device.isMulti) : undefined
        );
      }
    }
  }, [deviceList, deviceLoading, selectedSite]);

  // Prune parameter selection based on available parameterList
  useEffect(() => {
    if (isEmpty(selectedDevice)) return;
    if (parameterLoading || !parameterList?.data) return;

    const rawVal = form.getFieldValue(getName(parameterName));
    const currentVal = getEntityIds(rawVal);
    const availableParamIds = parameterList.data.map((p) => p._id);

    if (!isEmpty(currentVal)) {
      const currentArray = Array.isArray(currentVal) ? currentVal : [currentVal];
      const validArray = currentArray.filter((id) => availableParamIds.includes(id));

      if (validArray.length !== currentArray.length) {
        form.setFieldValue(
          getName(parameterName),
          validArray.length ? normalizeValue(validArray, parameter.isMulti) : undefined
        );
      }
    }
  }, [parameterList, parameterLoading, selectedDevice]);

  // handle change
  const handleSelectChange = (vals, dataList, fieldName, clearFields = [], isMulti) => {
    let finalValues = vals;

    if (isMulti && vals?.includes("all")) {
      const allIds = dataList?.map((item) => item._id) || [];
      const selectedWithoutAll = vals.filter((v) => v !== "all");
      const isAllSelected = allIds.length && _.isEqual([...allIds].sort(), [...selectedWithoutAll].sort());
      finalValues = isAllSelected ? [] : allIds;
    }

    finalValues = normalizeValue(finalValues, isMulti);
    form.setFieldValue(getName(fieldName), finalValues);

    if (isEmpty(finalValues) || !isMulti) {
      clearFields.forEach((f) => {
        form.setFieldValue(getName(f), undefined);
      });
    }
  };

  // options builder
  const getOptions = (data, isMulti, currentValues, labelMapper) => {
    if (!Array.isArray(data)) return [];

    const base = data.map(labelMapper);

    if (isMulti && base.length > 0) {
      const allIds = data.map((i) => i._id);
      const isAllSelected = Array.isArray(currentValues) && _.isEqual([...allIds].sort(), [...currentValues].sort());

      base.unshift({
        label: isAllSelected ? "Remove All" : "Select All",
        value: "all",
      });
    }

    return base;
  };

  // while the list is loading, show "Loading..." instead of the raw business id
  const getBusinessOptions = () => {
    const options = getOptions(businessList?.data || [], business.isMulti, selectedBusiness, (item) => ({
      label: `${item.name} (${item.id})`,
      value: item._id,
    }));
    if (!businessLoading) return options;
    const selectedIds = [].concat(selectedBusiness || []);
    const missing = selectedIds.filter((id) => !options.some((o) => o.value === id));
    return [...options, ...missing.map((id) => ({ label: "Loading...", value: id }))];
  };

  return (
    <>
      {/* BUSINESS */}
      {business.show && (
        <Form.Item name={[...names, businessName]} label="Business" extra={business.extra} {...business.formItemsProps}>
          <Select
            mode={business.isMulti ? "multiple" : undefined}
            showSearch
            filterOption={filterOption}
            loading={businessLoading}
            maxTagCount="responsive"
            placeholder="Select Business"
            onChange={(vals) =>
              handleSelectChange(vals, businessList?.data || [], businessName, [siteName, deviceName, parameterName], business.isMulti)
            }
            disabled={businessLoading || (businessList?.data?.length === 1 && !isEmpty(selectedBusiness))}
            options={getBusinessOptions()}
            {...business.selectProps}
          />
        </Form.Item>
      )}

      {/* SITE */}
      {site.show && (
        <Form.Item name={[...names, siteName]} label="Site Location" extra={site.extra} {...site.formItemsProps}>
          <Select
            mode={site.isMulti ? "multiple" : undefined}
            showSearch={{ filterOption }}
            loading={siteLoading}
            maxTagCount="responsive"
            placeholder="Select Site"
            onChange={(vals) => handleSelectChange(vals, siteLocationList?.data || [], siteName, [deviceName, parameterName], site.isMulti)}
            options={getOptions(!isEmpty(selectedBusiness) ? siteLocationList?.data : [], site.isMulti, selectedSite, (item) => ({
              label: item.name,
              value: item._id,
            }))}
            {...site.selectProps}
          />
        </Form.Item>
      )}

      {/* DEVICE */}
      {device.show && (
        <Form.Item name={[...names, deviceName]} label="Device" extra={device.extra} {...device.formItemsProps}>
          <Select
            mode={device.isMulti ? "multiple" : undefined}
            showSearch={{ filterOption }}
            loading={deviceLoading}
            maxTagCount="responsive"
            placeholder="Select Device"
            onChange={(vals) => handleSelectChange(vals, deviceList?.data || [], deviceName, [parameterName], device.isMulti)}
            options={getOptions(!isEmpty(selectedSite) ? deviceList?.data : [], device.isMulti, selectedDevice, (item) => ({
              label: item.name,
              value: item._id,
            }))}
            {...device.selectProps}
          />
        </Form.Item>
      )}

      {/* PARAMETER */}
      {parameter.show && (
        <Form.Item name={[...names, parameterName]} label="Parameter" extra={parameter.extra} {...parameter.formItemsProps}>
          <Select
            mode={parameter.isMulti ? "multiple" : undefined}
            showSearch={{ filterOption }}
            loading={parameterLoading}
            maxTagCount="responsive"
            placeholder="Select Parameter"
            onChange={(vals) => handleSelectChange(vals, parameterList?.data || [], parameterName, [], parameter.isMulti)}
            options={getOptions(!isEmpty(selectedDevice) ? parameterList?.data : [], parameter.isMulti, selectedYAxis, (item) => ({
              label: `${item?.device?.site?.name} > ${item?.device?.name} > ${item.name}`,
              value: item._id,
            }))}
            {...parameter.selectProps}
          />
        </Form.Item>
      )}
    </>
  );
};

BusinessSerializeSelector.propTypes = {
  business: PropTypes.object,
  site: PropTypes.object,
  device: PropTypes.object,
  parameter: PropTypes.object,
  form: PropTypes.object.isRequired,
  names: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  listName: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
};

export default BusinessSerializeSelector;
